import React, { useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CornerDownLeft } from 'lucide-react';
import { Kbd } from '../ui/kbd';
import { cn } from '../../lib/utils';
import { COMPOSER_MAX_HEIGHT_PX, COMPOSER_MIN_ROWS } from '../../constants/chat';

interface ChatComposerInputProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onCompositionStart: () => void;
  onCompositionEnd: () => void;
  disabled?: boolean;
  /** Attached-file chips, shown inside the box above the text. */
  attachments?: React.ReactNode;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * The composer box: attachments, the text itself, and the send hint. Grows with
 * the content to a ceiling, then scrolls inside.
 */
const ChatComposerInput: React.FC<ChatComposerInputProps> = ({
  value,
  onChange,
  onKeyDown,
  onCompositionStart,
  onCompositionEnd,
  disabled,
  attachments,
  textareaRef,
}) => {
  const { t } = useTranslation();
  const hasAttachments = Boolean(attachments && React.Children.count(attachments) > 0);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Clearing height first lets the `rows` attribute set the natural floor, so
    // the minimum tracks the font rather than a hardcoded pixel guess.
    el.style.height = '';
    const floor = el.clientHeight;
    // Then measure the content, which shrinks as well as grows.
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, floor), COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [textareaRef]);

  // Layout effect, not an effect: measuring after paint shows one frame at the
  // stale height, which flickers on every keystroke that adds a line.
  useLayoutEffect(resize, [value, resize]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event);
    resize();
  };

  return (
    <div
      className={cn(
        'group/composer rounded-xl border bg-background transition-colors duration-150 ease-shell',
        'focus-within:border-ring/60 focus-within:ring-2 focus-within:ring-ring/20',
      )}
    >
      {hasAttachments && (
        <div className="flex flex-wrap items-center gap-1.5 border-b px-2 py-2">{attachments}</div>
      )}

      <div className="relative">
        <textarea
          ref={textareaRef}
          rows={COMPOSER_MIN_ROWS}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          placeholder={t('chat.inputPlaceholder')}
          aria-label={t('chat.inputPlaceholder')}
          aria-describedby="composer-send-hint"
          disabled={disabled}
          className={cn(
            'block w-full resize-none bg-transparent px-3.5 py-3 text-sm leading-relaxed',
            'outline-hidden placeholder:text-muted-foreground disabled:opacity-50',
            // Room for the hint, but only while it is actually there.
            value.trim() && 'pb-7',
          )}
        />

        {/* Appears with the first character — the moment Enter starts to mean
            something. Rendered persistently so aria-describedby always resolves. */}
        <span
          id="composer-send-hint"
          className={cn(
            'pointer-events-none absolute bottom-2 right-3 flex items-center gap-1',
            'text-[0.6875rem] text-muted-foreground transition-opacity duration-150',
            value.trim() ? 'opacity-100' : 'opacity-0',
          )}
        >
          <Kbd>
            <CornerDownLeft className="size-3" strokeWidth={2.2} />
          </Kbd>
          {t('chat.toSend')}
        </span>
      </div>
    </div>
  );
};

export default ChatComposerInput;
